import {Pill} from '@wandb/components';
import {TargetBlank} from '@wandb/weave/common/util/links';
import {list, varNode} from '@wandb/weave/core';
import React from 'react';

import {lookupDocUrl} from '../../../util/doc_urls';
import {
  PANEL_BANK_CHARTS_NAME,
  PANEL_BANK_EVALUATION_NAME,
  PANEL_BANK_MEDIA_NAME,
} from '../../../util/panelbankTypes';
import {type PanelConfig} from '../../../util/panelTypes';
import {CREATED_MARIMO_PANEL_FEATURE_KEY} from '../../../util/userContext';
import {type PanelViews, panelViews} from '../../PanelBank/types';
import {INITIAL_CONFIG as VEGA2_INITIAL_CONFIG} from '../../PanelVega2';
import type {
  AddPanelsMenuGroupProps,
  AddPanelsMenuItemProps,
  PanelType,
} from './types';

const QUICK_ADD_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'quick-add',
    iconName: 'panel-auto-gen',
    label: 'Quick panel builder',
    description: 'Use regex to quickly build panels from keys.',
  },
];

const LINE_ITEM: AddPanelsMenuItemProps = {
  type: 'line',
  iconName: 'chart-line-plot-multi',
  label: 'Line plot',
  description: 'Display data trends and changes over time.',
};

const BAR_ITEM: AddPanelsMenuItemProps = {
  type: 'bar',
  iconName: 'chart-vertical-bars',
  label: 'Bar chart',
  description: 'Compare categories using vertical or horizontal bars.',
};

const SCATTER_ITEM: AddPanelsMenuItemProps = {
  type: 'scatter',
  iconName: 'chart-scatterplot',
  label: 'Scatter plot',
  description: 'Display relationships and correlations between data.',
};

const PARALLEL_ITEM: AddPanelsMenuItemProps = {
  type: 'parallel',
  iconName: 'chart-parallel-coordinates',
  label: 'Parallel coordinates',
  description: 'Compare multiple parameters across several dimensions.',
};

const VEGA2_ITEM: AddPanelsMenuItemProps = {
  type: 'vega2',
  iconName: 'category-multimodal',
  label: 'Custom chart',
  description: 'Build custom visualizations using Vega.',
  isHiddenDefault: true,
};

const PLOTLY_ITEM: AddPanelsMenuItemProps = {
  type: 'plotly',
  iconName: 'plotly',
  label: 'Plotly',
  description: 'Display tailored visualizations using Plotly.',
  isHiddenDefault: true,
};

const SCALAR_ITEM: AddPanelsMenuItemProps = {
  type: 'scalar',
  iconName: 'type-number-alt',
  label: 'Scalar chart',
  description: 'Display a single numeric value or metric.',
  isHiddenDefault: true,
};

const BASE_BEFORE_SHOW_MORE_ITEMS: AddPanelsMenuItemProps[] = [
  LINE_ITEM,
  BAR_ITEM,
  SCATTER_ITEM,
  PARALLEL_ITEM,
];

const BASE_AFTER_SHOW_MORE_ITEMS: AddPanelsMenuItemProps[] = [
  VEGA2_ITEM,
  PLOTLY_ITEM,
  SCALAR_ITEM,
];

const CHARTS_ITEMS: AddPanelsMenuItemProps[] = [
  ...BASE_BEFORE_SHOW_MORE_ITEMS,
  // items below should be hidden under "show more" by default
  ...BASE_AFTER_SHOW_MORE_ITEMS,
];

const MARIMO_ITEM: AddPanelsMenuItemProps = {
  type: 'marimo',
  iconName: 'notebook',
  label: 'Marimo',
  newPillFeatureKey: CREATED_MARIMO_PANEL_FEATURE_KEY,
  description: 'Use a Python notebook to generate a custom visualization.',
};

const CHARTS_ITEMS_WITH_MARIMO: AddPanelsMenuItemProps[] = [
  ...BASE_BEFORE_SHOW_MORE_ITEMS,
  MARIMO_ITEM,
  ...BASE_AFTER_SHOW_MORE_ITEMS,
];

export const EVALUATION_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'comparer',
    iconName: 'table',
    label: 'Run comparer',
    description: 'Compare metrics across runs and highlight differences.',
  },
  {
    type: 'parameter-importance',
    iconName: 'code-alt',
    label: 'Parameter importance',
    description: 'Compare hyper-parameters linked to desired outcomes.',
  },
  {
    type: 'weave-trace',
    iconName: 'layout-tabs',
    label: 'Weave traces',
    description: 'View and analyze Weave traces for selected runs.',
  },
];

const MEDIA_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'object3D',
    iconName: 'cube-container',
    label: '3D objects',
    description: 'Display 3D objects.',
  },
  {
    type: 'image',
    iconName: 'photo-picture',
    label: 'Images',
    description: 'Display image data.',
  },
  {
    type: 'video',
    iconName: 'play',
    label: 'Video',
    description: 'Display video data.',
  },
  {
    type: 'audio',
    iconName: 'audio-volume',
    label: 'Audio',
    description: 'Display audio data',
  },
];

const TEXT_CODE_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'code',
    iconName: 'code-alt',
    label: 'Code',
    description: 'Display and compare code diffs across runs.',
  },
  {
    type: 'html',
    iconName: 'code-alt',
    label: 'HTML',
    description: 'Display HTML content directly.',
  },
  {
    type: 'markdown',
    iconName: 'text-language',
    label: 'Markdown',
    description: 'Add formatted notes to track insights and observations.',
  },
];

const QUERY_PANEL_ITEMS: AddPanelsMenuItemProps[] = [
  {
    type: 'panel-weave',
    iconName: 'search',
    label: 'Query panel',
    description: 'Query and visualize data interactively.',
  },
  {
    type: 'weave-table',
    iconName: 'table',
    label: 'Table',
    description: (
      <>
        Display data logged with{' '}
        <code className="rounded bg-oblivion/[0.04] p-3 font-[Inconsolata]">
          wandb.Table()
        </code>{' '}
        as a table.
      </>
    ),
  },
  {
    type: 'weave-plot',
    iconName: 'chart-scatterplot',
    label: 'Scatter plot',
    description: (
      <>
        Display data logged with{' '}
        <code className="rounded bg-oblivion/[0.04] p-3 font-[Inconsolata]">
          wandb.Table()
        </code>{' '}
        as a scatter plot.
      </>
    ),
  },
  {
    type: 'weave-projector',
    iconName: 'play',
    label: 'Embedding projector',
    description: 'Display high-dimensional data as vectors.',
  },
];

const CHARTS_SECTION: AddPanelsMenuGroupProps = {
  title: PANEL_BANK_CHARTS_NAME,
  iconColor: 'blue',
  items: CHARTS_ITEMS,
  isExpandedDefault: true,
};

const CHARTS_SECTION_WITH_MARIMO: AddPanelsMenuGroupProps = {
  ...CHARTS_SECTION,
  items: CHARTS_ITEMS_WITH_MARIMO,
};

const EVALUATION_SECTION: AddPanelsMenuGroupProps = {
  title: PANEL_BANK_EVALUATION_NAME,
  iconColor: 'purple',
  items: EVALUATION_ITEMS,
  isExpandedDefault: true,
};

const QUERY_PANELS_SECTION: AddPanelsMenuGroupProps = {
  title: 'Query panels',
  description: (
    <>
      Learn more about query panels{' '}
      <TargetBlank href={lookupDocUrl('query-panel')}>in W&B docs</TargetBlank>.
    </>
  ),
  iconColor: 'magenta',
  items: QUERY_PANEL_ITEMS,
};

const MEDIA_SECTION: AddPanelsMenuGroupProps = {
  title: PANEL_BANK_MEDIA_NAME,
  iconColor: 'green',
  items: MEDIA_ITEMS,
};

const TEXT_CODE_SECTION: AddPanelsMenuGroupProps = {
  title: 'Text and code',
  iconColor: 'teal',
  items: TEXT_CODE_ITEMS,
};

export const ADD_PANELS_MENU_GROUPS: AddPanelsMenuGroupProps[] = [
  CHARTS_SECTION,
  EVALUATION_SECTION,
  QUERY_PANELS_SECTION,
  MEDIA_SECTION,
  TEXT_CODE_SECTION,
];

export const ADD_PANELS_MENU_GROUPS_WITH_MARIMO: AddPanelsMenuGroupProps[] = [
  CHARTS_SECTION_WITH_MARIMO,
  EVALUATION_SECTION,
  QUERY_PANELS_SECTION,
  MEDIA_SECTION,
  TEXT_CODE_SECTION,
];

export const ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD: AddPanelsMenuGroupProps[] =
  [
    {
      items: QUICK_ADD_ITEMS,
      iconColor: 'cactus',
    },
    ...ADD_PANELS_MENU_GROUPS,
  ];

export const ADD_PANELS_MENU_GROUPS_WITH_QUICK_ADD_AND_MARIMO: AddPanelsMenuGroupProps[] =
  [
    {
      items: QUICK_ADD_ITEMS,
      iconColor: 'cactus',
    },
    ...ADD_PANELS_MENU_GROUPS_WITH_MARIMO,
  ];

export const PANEL_VIEWS_BY_PANEL_TYPE: Partial<Record<PanelType, PanelViews>> =
  {
    bar: panelViews.barChart,
    code: panelViews.codeComparer,
    comparer: panelViews.runComparer,
    line: panelViews.runHistoryLinePlot,
    marimo: panelViews.marimo,
    markdown: panelViews.markdown,
    'panel-weave': panelViews.weave,
    parallel: panelViews.parallelCoordinatesPlot,
    'parameter-importance': panelViews.parameterImportance,
    scalar: panelViews.scalarChart,
    scatter: panelViews.scatterPlot,
    vega2: panelViews.vega2,
    'weave-plot': panelViews.weave,
    'weave-projector': panelViews.weave,
    'weave-table': panelViews.weave,
    'weave-trace': panelViews.weaveTrace,
  };

export const DEFAULT_CONFIGS_BY_PANEL_TYPE: Partial<
  Record<PanelType, PanelConfig>
> = {
  vega2: VEGA2_INITIAL_CONFIG,
  'panel-weave': {
    panel2Config: {
      autoFocus: true,
    },
  },
  'weave-plot': {
    panel2Config: {
      exprAndPanelLocked: true,
      panelId: 'runs-table.row.plot',
      exp: varNode(list('run' as const), 'runs'),
    },
  },
  'weave-projector': {
    panel2Config: {
      exprAndPanelLocked: true,
      panelId: 'runs-table.row.projection.plot',
      exp: varNode(list('run' as const), 'runs'),
    },
  },
  'weave-table': {
    panel2Config: {
      exprAndPanelLocked: true,
      panelId: 'runs-table.row.table',
      exp: varNode(list('run' as const), 'runs'),
    },
  },
};
